import { getFromRoamingOrLocal, removeFromStorage, flushRoaming } from "@/shared/services/storage";
import { info, warn } from "@/shared/services/logger";

const SOURCE = "setup-state";
const ROAMING_KEY = "helpitech_setup_complete";
const LOCAL_KEY = "setup_complete";

/** Chaves do snapshot legado, usadas só para migrar instalações antigas. */
const LEGACY_ROAMING_KEY = "helpitech_user_info";
const LEGACY_LOCAL_KEY = "user_info";

/**
 * Registro durável de que o usuário já concluiu a configuração da assinatura.
 *
 * Existe porque o portão de onboarding não pode depender do snapshot em
 * {@link userDataCache}: aquele snapshot é derivado do Microsoft Graph e
 * descartável por natureza (é recomposto a cada login, some quando o Graph
 * falha, e o `roamingSettings` pode ser revertido por outro runtime). Usá-lo
 * como prova de configuração fazia o suplemento pedir "Configurar Assinatura"
 * de novo a cada abertura do painel.
 *
 * Diferente do snapshot, esta flag só é apagada no logout.
 *
 * @example
 * ```typescript
 * import { setupState } from "@/shared/services/setup-state";
 *
 * router.push(setupState.isComplete() ? "/signatures" : "/profile");
 * ```
 */
export const setupState = {
  /**
   * Indica se a configuração já foi concluída ao menos uma vez.
   *
   * Instalações anteriores à flag são migradas na leitura: um snapshot já
   * gravado prova que o usuário passou pelo onboarding, então a flag é
   * promovida e o estado se cura sozinho — ninguém é reonboardado pelo upgrade.
   *
   * @returns `true` quando o usuário já está configurado.
   *
   * @example
   * if (!setupState.isComplete()) router.push("/profile");
   */
  isComplete(): boolean {
    if (getFromRoamingOrLocal(ROAMING_KEY, LOCAL_KEY) === "true") return true;

    const legacy = getFromRoamingOrLocal(LEGACY_ROAMING_KEY, LEGACY_LOCAL_KEY);
    if (legacy && legacy !== "null") {
      info(SOURCE, "isComplete: snapshot legado encontrado — promovendo a flag de configuração.");
      this.markComplete();
      void flushRoaming();
      return true;
    }

    return false;
  },

  /**
   * Marca a configuração como concluída, nos dois armazenamentos.
   *
   * Não chama `saveAsync`: quem disparou o fluxo é que persiste o roaming,
   * para evitar gravações concorrentes.
   *
   * @example
   * setupState.markComplete();
   */
  markComplete(): void {
    try {
      localStorage.setItem(LOCAL_KEY, "true");
    } catch {
      warn(SOURCE, "markComplete: erro ao salvar no localStorage.");
    }

    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      try {
        Office.context.roamingSettings.set(ROAMING_KEY, "true");
      } catch {
        warn(SOURCE, "markComplete: erro ao salvar no roamingSettings.");
      }
    }
  },

  /**
   * Remove a flag. Só o logout deve chamar — `userDataCache.clear()` (usado por
   * "Restaurar padrões") deliberadamente a preserva.
   *
   * @example
   * setupState.clear();
   */
  clear(): void {
    removeFromStorage(ROAMING_KEY, LOCAL_KEY);
    info(SOURCE, "clear: flag de configuração removida.");
  },
};
